import { useEffect } from "react";
import { AutocompleteInput, BooleanInput, ReferenceInput } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import type { SadaratGender } from "@/utils/sadarat-gender";

function SadaratGenderSlot({
  needSource,
  idSource,
  gender,
  needLabel,
  showWhen,
}: {
  needSource: "needSadaratMale" | "needSadaratFemale";
  idSource: "sadaratMaleId" | "sadaratFemaleId";
  gender: SadaratGender;
  needLabel: string;
  /** Male defaults on (`!== false`); female only when explicitly true. */
  showWhen: "defaultOn" | "explicitOn";
}) {
  const need = useWatch({ name: needSource });
  const { setValue } = useFormContext();
  const visible = showWhen === "defaultOn" ? need !== false : need === true;

  useEffect(() => {
    if (!visible) {
      setValue(idSource, null, { shouldDirty: true, shouldValidate: false });
    }
  }, [visible, idSource, setValue]);

  return (
    <>
      <BooleanInput
        source={needSource}
        label={needLabel}
        helperText={
          showWhen === "defaultOn"
            ? "When both Male and Female are off, sadarat is not assigned and this event is not synced to Google Calendar"
            : false
        }
      />
      {visible ? (
        <ReferenceInput
          source={idSource}
          reference="sadarats"
          perPage={100}
          allowEmpty
          filter={{ gender }}
        >
          <AutocompleteInput
            optionText={(r) =>
              r?.itsNo ? `${String(r.itsNo)} — ${r.name || ""}` : String(r?.name ?? "")
            }
            label={`${gender === "MALE" ? "Male" : "Female"} sadarat (optional)`}
            emptyText="— None —"
            fullWidth
          />
        </ReferenceInput>
      ) : null}
    </>
  );
}

/** Dual toggles + gender-filtered sadarat pickers; clears each id when its need flag is off. */
export function NeedSadaratFields() {
  return (
    <>
      <SadaratGenderSlot
        needSource="needSadaratMale"
        idSource="sadaratMaleId"
        gender="MALE"
        needLabel="Needs sadarat (Male)"
        showWhen="defaultOn"
      />
      <SadaratGenderSlot
        needSource="needSadaratFemale"
        idSource="sadaratFemaleId"
        gender="FEMALE"
        needLabel="Needs sadarat (Female)"
        showWhen="explicitOn"
      />
    </>
  );
}
